import { useMemo, type ReactNode } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipProps,
} from 'recharts';
import { useTranslation } from '../../i18n/useTranslation';
import { Text } from '../atoms/Typography/Text';
import { formatMeasuredAt } from '../../utils/measurementDate';
import {
  entryForDay,
  niceDomain,
  percentOfTarget,
  sharedTimeline,
  timeDomain,
  usePrefersReducedMotion,
  valuesOnTimeline,
  type ChartSeries,
  type TimelinePoint,
} from './historyChartUtils';
import { formatDay, useChartColors, useSeriesColors } from './MeasurementTrendChart';
import { ParameterSwatch } from './ParameterSwatch';
import { formatDate, formatNumber } from './measurementHistoryUtils';
import './MeasurementTrendChart.css';

type CombinedRow = { time: number } & Record<string, number | null>;

const seriesKey = (index: number): string => `s${index}`;

/** One combined-chart row per timestamp: each series' value as % of its target range (null where it has none). */
export function combinedRows(series: readonly ChartSeries[], timeline: readonly TimelinePoint[]): CombinedRow[] {
  const columns = series.map((s) =>
    valuesOnTimeline(s.points, timeline).map((v) => (v === null || !s.target ? null : percentOfTarget(v, s.target))),
  );
  return timeline.map((p, row) => {
    const out: CombinedRow = { time: p.time };
    columns.forEach((column, i) => {
      out[seriesKey(i)] = column[row] ?? null;
    });
    return out;
  });
}

export interface CombinedTooltipContentProps {
  series: readonly ChartSeries[];
  at: TimelinePoint;
}

/** Original values (not the percentages) of every parameter on the hovered day. */
export function CombinedTooltipContent({ series, at }: CombinedTooltipContentProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.language || 'de';
  return (
    <div className="measurement-trend-chart__tooltip" data-testid="combined-tooltip">
      <Text as="span" size="xs" color="secondary">
        {formatDay(at, language)}
      </Text>
      {series.map((s) => {
        const entry = entryForDay(s.points, at);
        const pct = entry && s.target ? percentOfTarget(entry.value, s.target) : null;
        return (
          <span key={s.parameterId} className="measurement-trend-chart__tooltip-row">
            <ParameterSwatch slot={s.slot} />
            <Text as="span" size="sm">
              {entry
                ? t('measurement.history.combined.tooltipValue', {
                    parameter: s.label,
                    value: `${formatNumber(entry.value, language)} ${s.unit}`.trim(),
                    percent: pct === null ? '–' : formatNumber(Math.round(pct), language),
                    date: formatMeasuredAt(entry, language),
                  })
                : t('measurement.history.combined.tooltipNoValue', { parameter: s.label })}
            </Text>
          </span>
        );
      })}
    </div>
  );
}

export interface MeasurementCombinedChartProps {
  /** 2–3 series, each with a target range. */
  series: readonly ChartSeries[];
  caption: ReactNode;
  height?: number;
}

/**
 * "Gemeinsam darstellen" (#1653): every selected parameter on one axis as %
 * of its own target range (0 % = lower, 100 % = upper bound — the shaded
 * band), so values of very different size and unit can be compared. The
 * tooltip shows the original values with their units. Visual only, like the
 * single charts (aria-hidden; the summaries and table are the text equivalent).
 */
export function MeasurementCombinedChart({ series, caption, height = 300 }: MeasurementCombinedChartProps) {
  const { i18n } = useTranslation();
  const language = i18n.language || 'de';
  const base = useChartColors(null);
  const lineColors = useSeriesColors(series.map((s) => s.slot));
  const reducedMotion = usePrefersReducedMotion();

  const timeline = useMemo(() => sharedTimeline(series), [series]);
  const dayByTime = useMemo(() => new Map(timeline.map((p) => [p.time, p])), [timeline]);
  const data = useMemo(() => combinedRows(series, timeline), [series, timeline]);
  const domain = timeDomain(timeline);

  const yScale = useMemo(() => {
    const values: number[] = [0, 100];
    for (const row of data) {
      for (let i = 0; i < series.length; i += 1) {
        const v = row[seriesKey(i)];
        if (typeof v === 'number') values.push(v);
      }
    }
    return niceDomain(Math.min(...values), Math.max(...values), null);
  }, [data, series.length]);

  const renderTooltip = ({ active, label }: TooltipProps<number, string>) => {
    const at = active && typeof label === 'number' ? dayByTime.get(label) : undefined;
    return at ? <CombinedTooltipContent series={series} at={at} /> : null;
  };

  return (
    <figure className="measurement-trend-chart">
      <figcaption className="measurement-trend-chart__caption">{caption}</figcaption>
      <div
        className="measurement-trend-chart__plot"
        aria-hidden="true"
        data-testid="measurement-combined-chart"
        data-animated={reducedMotion ? 'false' : 'true'}
      >
        <ResponsiveContainer width="100%" height={height}>
          <LineChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
            <CartesianGrid stroke={base.grid} strokeDasharray="3 3" vertical={false} />
            <ReferenceArea y1={0} y2={100} fill={base.band} fillOpacity={1} stroke={base.bandEdge} strokeOpacity={0.4} />
            <XAxis
              dataKey="time"
              type="number"
              scale="time"
              domain={domain ?? ['dataMin', 'dataMax']}
              tickFormatter={(value: number) => formatDate(value, language)}
              stroke={base.axis}
              tick={{ fill: base.axis, fontSize: 12 }}
              tickLine={false}
              minTickGap={24}
            />
            <YAxis
              domain={yScale.domain}
              ticks={yScale.ticks}
              tickFormatter={(value: number) => `${formatNumber(value, language)} %`}
              stroke={base.axis}
              tick={{ fill: base.axis, fontSize: 12 }}
              tickLine={false}
              width={64}
            />
            <Tooltip content={renderTooltip} cursor={{ stroke: base.axis, strokeDasharray: '3 3' }} isAnimationActive={!reducedMotion} />
            {series.map((s, i) => (
              <Line
                key={s.parameterId}
                type="linear"
                dataKey={seriesKey(i)}
                name={s.label}
                stroke={lineColors[i] || base.series}
                strokeWidth={2}
                strokeDasharray={i === 1 ? '6 3' : i === 2 ? '2 3' : undefined}
                dot={{ r: 4, fill: lineColors[i] || base.series, stroke: base.surface, strokeWidth: 2 }}
                activeDot={{ r: 6, fill: lineColors[i] || base.series, stroke: base.surface, strokeWidth: 2 }}
                isAnimationActive={!reducedMotion}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </figure>
  );
}

export default MeasurementCombinedChart;
